import Phaser from "phaser";
import { AssetLocator } from "../sprites/AssetLocator";
import { Cat } from "../sprites/CatSprite";
import { HeartSprite } from "../sprites/HeartSprite";
import { CoinSprite } from "../sprites/CoinSprite";
import { PortalSprite } from "../sprites/PortalSprite";
import { BACKGROUNDS, BACKGROUND_DEFAULT } from "../../assets/backgrounds";

export class PreloadScene extends Phaser.Scene {
  /** Static flag survives scene restarts within the same JavaScript runtime */
  private static loadedOnce = false;

  constructor() {
    super({ key: "PreloadScene" });
  }

  preload() {
    // Allow React side to hint which scene should start after preload via global var.
    const hintedTarget =
      typeof window !== "undefined" && (window as any).__targetScene
        ? (window as any).__targetScene
        : undefined;
    if (hintedTarget) {
      this.registry.set("targetScene", hintedTarget);
    }
    // -- DEBUG -------------------------------------------------------------
    console.log("PreloadScene: static loadedOnce =", PreloadScene.loadedOnce);
    // ---------------------------------------------------------------------

    // If we've already loaded textures once in this JS runtime, fast-path directly.
    if (PreloadScene.loadedOnce) {
      const targetScene =
        this.registry.get("targetScene") || "BudgetSceneReact";
      console.log(
        `PreloadScene: Assets already cached – fast-path to ${targetScene}`,
      );

      const initialCategories = this.registry.get("initialCategories") || [];
      if (targetScene === "BudgetSceneReact") {
        this.scene.start("BudgetSceneReact", { categories: initialCategories });
      } else {
        this.scene.start(targetScene);
      }
      return;
    }

    // Create loading bar
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Position loading UI near the bottom so it appears in the black letter-box area on mobile
    const BAR_Y = height - 220; // 220px above the bottom edge

    const progressBar = this.add.graphics();
    const progressBox = this.add.graphics();
    progressBox.fillStyle(0x222222, 0.8);
    progressBox.fillRect(width / 4, BAR_Y, width / 2, 50);

    // Loading text
    const loadingText = this.make.text({
      x: width / 2,
      y: BAR_Y + 70,
      text: "Loading...",
      style: {
        font: "20px monospace",
        color: "#ffffff",
      },
    });
    loadingText.setOrigin(0.5, 0.5);

    // Progress text
    const percentText = this.make.text({
      x: width / 2,
      y: BAR_Y + 90,
      text: "0%",
      style: {
        font: "18px monospace",
        color: "#ffffff",
      },
    });
    percentText.setOrigin(0.5, 0.5);

    // Update progress bar as assets load
    this.load.on("progress", (value: number) => {
      percentText.setText(`${Math.floor(value * 100)}%`);
      progressBar.clear();
      progressBar.fillStyle(0xffffff, 1);
      progressBar.fillRect(
        width / 4 + 10,
        BAR_Y + 10,
        (width / 2 - 20) * value,
        30,
      );
    });

    // Clean up when loading is complete
    this.load.on("complete", () => {
      progressBar.destroy();
      progressBox.destroy();
      loadingText.destroy();
      percentText.destroy();
    });

    // Load cat & accessory atlases for every animation so downstream scenes never have to wait.
    Cat.queueAllAssets(this, AssetLocator.getAvailableAnimations());

    // Ensure loader uses anonymous CORS for remote assets
    (this.load as any).crossOrigin = "anonymous";

    // Preload only the default background synchronously for fast startup
    const defaultCfg = BACKGROUNDS[BACKGROUND_DEFAULT];
    this.load.image("bg_" + BACKGROUND_DEFAULT, defaultCfg.path);

    const allAnimations = AssetLocator.getAvailableAnimations(); // keep for effects section

    // Load misc single-sheet assets via their own classes
    HeartSprite.preload(this);
    CoinSprite.preload(this);
    PortalSprite.preload(this);

    // Load effect assets
    console.log("PreloadScene: Loading effect assets");
    allAnimations.forEach((animation) => {
      const effectAsset = AssetLocator.getEffectAsset(animation);
      if (!effectAsset) return;

      const inCache = this.textures.exists(effectAsset.key);
      console.log(`Effect ${effectAsset.key} already in cache?`, inCache);

      if (!inCache) {
        console.log(`Loading effect asset: ${effectAsset.key}`);
        this.load.atlas(
          effectAsset.key,
          `assets/${effectAsset.imagePath}`,
          `assets/${effectAsset.jsonPath}`,
        );
      }
    });
  }

  async create() {
    // Wait for cat assets to load
    await new Promise<void>((resolve) => {
      // Detach old progress/complete listeners to avoid callbacks accessing destroyed objects
      this.load.removeAllListeners("progress");
      this.load.removeAllListeners("complete");

      this.load.once("complete", () => resolve());
      if (this.load.isLoading()) {
        this.load.start();
      } else {
        resolve();
      }
    });

    // Create misc animations
    HeartSprite.createAnimation(this);
    CoinSprite.createAnimation(this);
    PortalSprite.createAnimations(this);

    // Create cat animations through CatSprite helper (priority)
    Cat.createAllAnimations(this, AssetLocator.getAvailableAnimations());

    // Effects may still need full animation list
    const allAnimationsFull = AssetLocator.getAvailableAnimations();

    // Create effect animations
    console.log("PreloadScene: Creating effect animations");
    allAnimationsFull.forEach((animation) => {
      const effectAsset = AssetLocator.getEffectAsset(animation);
      if (
        effectAsset &&
        this.textures.exists(effectAsset.key) &&
        !this.anims.exists(effectAsset.key)
      ) {
        const frames = this.anims
          .generateFrameNames(effectAsset.key)
          .sort((a, b) => {
            const getIdx = (f: Phaser.Types.Animations.AnimationFrame) => {
              const match = String(f.frame).match(/_(\d+)\.png$/);
              return Number(match?.[1] ?? "0");
            };
            return getIdx(a) - getIdx(b);
          });

        this.anims.create({
          key: effectAsset.key,
          frames,
          frameRate: 24,
          repeat: -1, // Infinite loop
        });

        console.log(
          `Created effect animation for: ${effectAsset.key} with ${frames.length} frames`,
        );
      } else if (effectAsset) {
        console.warn("Effect texture not found:", effectAsset.key);
      }
    });

    // Mark as loaded for future PreloadScene runs
    PreloadScene.loadedOnce = true;
    // ---------------------------------------------------------------------

    // Signal that assets are loaded and start BudgetSceneReact
    const targetScene = this.registry.get("targetScene") || "BudgetSceneReact";
    console.log(`PreloadScene: Assets loaded, starting ${targetScene}`);

    // Get initial data from registry or use defaults
    const initialCategories = this.registry.get("initialCategories") || [];

    if (targetScene === "BudgetSceneReact") {
      // Launch game scene without shutting down PreloadScene, so we can continue loading in the background
      this.scene.launch("BudgetSceneReact", { categories: initialCategories });

      // Preload remaining backgrounds asynchronously
      this.load.reset();
      (this.load as any).crossOrigin = "anonymous";

      Object.entries(BACKGROUNDS).forEach(([name, cfg]) => {
        if (name === BACKGROUND_DEFAULT) return;
        const key = "bg_" + name;
        // Double-check texture doesn't exist to avoid "already in use" warnings
        if (this.textures.exists(key)) {
          console.log(
            `Background texture ${key} already exists, skipping load`,
          );
          return;
        }
        this.load.image(key, cfg.path);
      });

      if (this.load.totalToLoad > 0) {
        console.log("PreloadScene: Backgrounds loading in parallel");
        this.load.once("complete", () => {
          console.log("PreloadScene: All backgrounds cached");
          // We can safely stop this scene now
          this.scene.stop();
        });
        this.load.start();
      } else {
        // Nothing to load, stop immediately
        this.scene.stop();
      }
    } else {
      this.scene.start(targetScene);
    }
  }
}
